import { Avatar, FlexBox } from '@dixa/lib-shared-habla';
import { animated, config, useSpring, useTrail } from '@react-spring/web';
import { ampli } from 'lib-shared-tracking';
import {
  CSSProperties,
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { appActions } from 'app/app.actions';
import { rewindConfig } from 'components/rewind';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { useRewindContext } from '../../../rewindModal.context';
import { RewindSlideProps } from '../../../rewindModal.types';
import { getRewindSummary } from '../../../utils';
import { LinkedInLogo } from '../../rewindControls/components/rewindSocialLogos';
import { SlideContainer } from '../../slideContainer';

import styles from './closingSlide.module.scss';

// Animation timing constants
const TIMING = {
  AVATAR_APPEAR: 300,
  GREETING_APPEAR: 1000,
  THANK_YOU_APPEAR: 2000,
  MESSAGE_APPEAR: 3200,
  TAGLINE_APPEAR: 4500,
  SHARE_APPEAR: 5500,
  STARS_APPEAR: 6500,
  MUSIC_FADE: 10000,
};

export const ClosingSlide = memo(function ClosingSlide({
  isCompleted,
  onComplete,
  onFadeMusic,
}: RewindSlideProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { data, user } = useRewindContext();

  const [phase, setPhase] = useState(isCompleted ? 8 : 0);
  const [copyTextSuccess, setCopyTextSuccess] = useState(false);

  // Reset copy success state after delay
  useEffect(() => {
    if (copyTextSuccess) {
      const timer = setTimeout(() => setCopyTextSuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copyTextSuccess]);

  const handleCopyText = useCallback(async () => {
    if (!data) return;

    try {
      const summary = getRewindSummary(data);

      await navigator.clipboard.writeText(summary);
      setCopyTextSuccess(true);

      ampli.rewindSlideTextCopied({
        year: rewindConfig.year,
        slide: 'closing',
      });
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  }, [data]);

  const handleShareLinkedIn = useCallback(() => {
    if (!data) return;

    const summary = getRewindSummary(data);

    ampli.rewindShareToLinkedInClicked({
      year: rewindConfig.year,
      slide: 'closing',
    });

    window.open(
      `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(summary)}`,
      '_blank',
    );
  }, [data]);

  const handleSendFeedback = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.stopPropagation();

      ampli.rewindFeedbackClicked({
        year: rewindConfig.year,
        slide: 'closing',
      });

      dispatch(
        appActions.showModal({
          modal: {
            type: 'app:feedbackForm',
            arg: {
              title: `Dixa Rewind ${rewindConfig.year} Feedback`,
              team: 'insights',
              placeholder:
                'Tell us what you think about Dixa Rewind! Any suggestions or issues?',
              feature: 'rewind',
              extra: {
                rewindData: JSON.stringify(data),
              },
            },
          },
        }),
      );
    },
    [data, dispatch],
  );

  // Start animation sequence
  useEffect(() => {
    // If slide has completed before, skip animations
    if (isCompleted) {
      setPhase(8);
      return;
    }

    const timers = [
      setTimeout(() => setPhase(1), TIMING.AVATAR_APPEAR),
      setTimeout(() => setPhase(2), TIMING.GREETING_APPEAR),
      setTimeout(() => setPhase(3), TIMING.THANK_YOU_APPEAR),
      setTimeout(() => setPhase(4), TIMING.MESSAGE_APPEAR),
      setTimeout(() => setPhase(5), TIMING.TAGLINE_APPEAR),
      setTimeout(() => setPhase(6), TIMING.SHARE_APPEAR),
      setTimeout(() => setPhase(7), TIMING.STARS_APPEAR),
      setTimeout(() => {
        setPhase(8);

        // Start fading the music over 30 seconds
        onFadeMusic?.(0, 30000);

        onComplete?.('closing');
      }, TIMING.MUSIC_FADE),
    ];

    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompleted]);

  // Avatar animation
  const avatarSpring = useSpring({
    opacity: phase >= 1 ? 1 : 0,
    transform:
      phase >= 1 ? 'scale(1) rotateY(0deg)' : 'scale(0.3) rotateY(180deg)',
    config: { tension: 200, friction: 20 },
    immediate: isCompleted,
  });

  // Greeting animation
  const greetingSpring = useSpring({
    opacity: phase >= 2 ? 1 : 0,
    transform: phase >= 2 ? 'translateY(0px)' : 'translateY(20px)',
    config: config.gentle,
    immediate: isCompleted,
  });

  // Thank you title animation
  const thankYouSpring = useSpring({
    opacity: phase >= 3 ? 1 : 0,
    transform:
      phase >= 3 ? 'scale(1) translateY(0px)' : 'scale(0.5) translateY(50px)',
    config: { tension: 120, friction: 14 },
    immediate: isCompleted,
  });

  // Message animation
  const messageSpring = useSpring({
    opacity: phase >= 4 ? 1 : 0,
    transform: phase >= 4 ? 'translateY(0px)' : 'translateY(30px)',
    config: config.gentle,
    immediate: isCompleted,
  });

  // Tagline words trail
  const taglineWords = ['Dixa', 'Rewind', `${rewindConfig.year}`];
  const taglineTrail = useTrail(taglineWords.length, {
    opacity: phase >= 5 ? 1 : 0,
    transform:
      phase >= 5 ? 'translateY(0px) scale(1)' : 'translateY(40px) scale(0.8)',
    config: { tension: 150, friction: 14 },
    immediate: isCompleted,
  });

  // Share section animation
  const shareSpring = useSpring({
    opacity: phase >= 6 ? 1 : 0,
    transform:
      phase >= 6 ? 'translateY(0px) scale(1)' : 'translateY(30px) scale(0.9)',
    config: config.gentle,
    immediate: isCompleted,
  });

  // Shooting stars - memoize positions so they don't change on re-render
  const shootingStars = useMemo(
    () =>
      [...Array(6)].map((_, i) => ({
        id: i,
        delay: i * 0.4, // Stagger more for subtler effect
        startX: 10 + Math.random() * 80,
        startY: Math.random() * 30,
      })),
    [],
  );
  const showStars = phase >= 7;

  return (
    <SlideContainer className={isCompleted ? styles.completed : undefined}>
      {/* Disco floor grid */}
      <div className={styles.discoFloor}>
        <div className={styles.gridLines} />
      </div>

      {/* Shooting stars */}
      {showStars && (
        <div className={styles.starsContainer}>
          {shootingStars.map((star) => (
            <div
              key={star.id}
              className={styles.shootingStar}
              style={
                {
                  '--delay': `${star.delay}s`,
                  '--start-x': `${star.startX}%`,
                  '--start-y': `${star.startY}%`,
                } as CSSProperties
              }
            />
          ))}
        </div>
      )}

      {/* Main centered content */}
      <div className={styles.mainContent}>
        {/* Avatar section */}
        <animated.div className={styles.avatarSection} style={avatarSpring}>
          <div className={styles.avatarContainer}>
            <div className={styles.avatarGlow} />
            <Avatar user={user} size="40" className={styles.avatar} />
            <div className={styles.avatarRing} />
          </div>
        </animated.div>

        {/* Greeting */}
        <animated.p className={styles.greeting} style={greetingSpring}>
          <span className={styles.userName}>
            {user.given_name ?? user.name}
          </span>
          , you crushed it!
        </animated.p>

        {/* Thank You Title */}
        <animated.h1 className={`${styles.title}`} style={thankYouSpring}>
          THANK YOU
        </animated.h1>

        {/* Appreciation message */}
        <animated.p className={styles.message} style={messageSpring}>
          For every conversation, every solution, and every smile you brought to
          life
        </animated.p>

        {/* Decorative line */}
        <div
          className={`${styles.decorativeLine} ${phase >= 5 ? styles.visible : ''}`}
        />

        {/* Tagline */}
        <div className={styles.tagline}>
          {taglineTrail.map((style, index) => {
            const word = taglineWords[index];
            const className =
              word === `${rewindConfig.year}`
                ? styles.year
                : styles[word.toLowerCase()] || styles.taglineWord;
            return (
              <animated.span key={word} className={className} style={style}>
                {word}
              </animated.span>
            );
          })}
        </div>
      </div>

      {/* Share section */}
      <animated.div className={styles.shareSection} style={shareSpring}>
        <p className={styles.sharePrompt}>Share your wins</p>
        <div className={styles.shareButtons}>
          <button
            className={styles.shareButton}
            onClick={handleShareLinkedIn}
            aria-label="Share on LinkedIn"
          >
            <LinkedInLogo />
            <span>Share on LinkedIn</span>
          </button>
          <button
            className={`${styles.shareButton} ${styles.copyButton} ${copyTextSuccess ? styles.success : ''}`}
            onClick={handleCopyText}
            aria-label="Copy summary"
          >
            <span className={styles.copyIcon}>
              {copyTextSuccess ? '✓' : '📋'}
            </span>
            <span>{copyTextSuccess ? 'Copied!' : 'Copy Summary'}</span>
          </button>
        </div>

        <FlexBox alignItems="center" gap="250" className={styles.shareTip}>
          <div>
            {rewindConfig.canGenerateImages
              ? '💡 Copy or save a screenshot of each slide from the bottom right'
              : '💡 Use Chrome to easily copy or save a screenshot of each slide'}
          </div>
          &bull;
          <a
            style={{ color: 'inherit', textDecoration: 'underline' }}
            onClick={handleSendFeedback}
          >
            Send feedback
          </a>
        </FlexBox>
      </animated.div>
    </SlideContainer>
  );
});
